import { FieldValues, UseFormReturn } from "react-hook-form";
import { forwardRef, useImperativeHandle } from "react";
import { getGroupOptionsServicesProvided } from "../utils";
import { FormatedField, FormatedFields } from "../../../../types/fields.types";
import { ServiceProvided, ServicesProvidedForm } from "../types";
import { useServicesProvidedFormHandler } from "../useServicesProvidedFormHandler";
import { Box, IconButton, Paper } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { renderFieldInput } from "../../../../components/fieldsInputs/renderFieldInput";
import { getFieldExtraOption } from "../../../../utils/getFieldExtraOption";
import { swapTwoFields } from "../../../../utils/formatFields";

export type ServiceProvidedInputRef = {
	submitFn: (formData: ServicesProvidedForm) => Promise<void>;
};

type ServicesProvidedInputProps = {
	treatmentPrescriptionId: number | undefined;
	entityForm: UseFormReturn<FieldValues>;
	fieldsServicesProv: FormatedFields;
	servicesProvided: Array<ServiceProvided>;
	selectedServiceProvidedId: number | undefined;
	selectedServiceProvided: ServiceProvided | undefined;
};

export const ServicesProvidedInput = forwardRef<ServiceProvidedInputRef, ServicesProvidedInputProps>(
	(
		{
			treatmentPrescriptionId,
			entityForm,
			fieldsServicesProv,
			servicesProvided,
			selectedServiceProvidedId,
			selectedServiceProvided,
		},
		ref
	) => {
		const { submitFn, handleDelete } = useServicesProvidedFormHandler(
			treatmentPrescriptionId,
			fieldsServicesProv,
			servicesProvided,
			selectedServiceProvidedId,
		);

		const isPeriodicityApplicable = entityForm.watch("session_periodicity") !== "not_applicable"

		const serviceProvidedExtraOptions = getGroupOptionsServicesProvided(true, isPeriodicityApplicable).fieldsWithExtraOptions;

		useImperativeHandle(ref, () => ({
			submitFn,
		}));

		const isDateField = (field: FormatedField) => field.field === "date_begin" || field.field === "date_end";

		const groupedNonDateFields = fieldsServicesProv
			.filter((fieldSP) => fieldSP.settings.group)
			.filter((field) => !isDateField(field));

		const groupedNonDateFieldsOrdered = swapTwoFields(
			groupedNonDateFields,
			"session_periodicity",
			"session_quantity"
		);

		return (
			<Paper elevation={4} sx={{ minHeight: "150px", p:4, borderRadius:4 }}>
				<Box p={4} display="flex" flexDirection="row">
					<Box
						display="flex"
						flex={1}
						justifyContent="space-between"
						alignItems="flex-start"
						flexDirection="column"
						gap={4}
					>
						<Box display="flex" flexDirection="row" gap={2}>
							{fieldsServicesProv.filter(isDateField).map((fieldSP) => {
								const fieldExtraOptions = getFieldExtraOption(
									serviceProvidedExtraOptions ?? [],
									fieldSP
								);
								return (
									<Box key={fieldSP.field}>
										{renderFieldInput(
											fieldSP,
											selectedServiceProvided,
											"group",
											entityForm as unknown as UseFormReturn<FieldValues>,
											fieldExtraOptions
										)}
									</Box>
								);
							})}
						</Box>
						<Box display="flex" flexDirection="row" gap={2} flex={1} width={"100%"}>
						{groupedNonDateFieldsOrdered.map((fieldSP) => {
									const fieldExtraOptions = getFieldExtraOption(
										serviceProvidedExtraOptions ?? [],
										fieldSP
									);

									return (
										<Box key={fieldSP.field} width={"100%"}>
											{renderFieldInput(
												fieldSP,
												selectedServiceProvided,
												"group",
												entityForm as unknown as UseFormReturn<FieldValues>,
												fieldExtraOptions
											)}
										</Box>
									);
						})}
						</Box>
					</Box>
					{selectedServiceProvided?.id && (
						<Box>
							<IconButton
								sx={{ transform: "translate(20px, -20px)" }}
								onClick={() => handleDelete(selectedServiceProvided?.id)}
								size="large"
								color="error"
							>
								<DeleteIcon />
							</IconButton>
						</Box>
					)}
				</Box>
			</Paper>
		);
	}
);
